import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaAngleRight, FaAngleLeft } from "react-icons/fa6";
import { ShoppingCart, CheckCircle2 } from "lucide-react";
import { IMG_URl } from "../../common/img_url";
import Visibility from "../utils/Visibility";
import { useTranslation } from "react-i18next";
import { indexStore } from "../../store/indexStore";
import OptionsProduct from "../OptionsProduct/OptionsProduct";
import { Skeleton } from "../ui/skeleton";
import ProductModel from "../../Models/PorductModel";
import UAEDirhamIcon from "../utils/UAEDirhamIcon";
import { AddToCart, CalcQuantity } from "../utils/CartFunctions";
import { toast } from "react-toastify";

function OldProductScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { name, description, product, itemLoading, lang } = indexStore();
  const params = useParams();

  const [optionsSelected, setOptionsSelected] = useState([]);
  const [quantity, setQuantity] = useState(1);

  const activeOrder = Number(
    localStorage.getItem(`active_order${params.branch_id || ""}`) || "0"
  );

  const handleBack = () => {
    navigate(-1);
  };

  const handleAddToCart = () => {
    AddToCart({
      element: { ...product, option: optionsSelected, note: "" },
      params: params,
      navigate: navigate,
      t: t,
      quantity: quantity,
    });
    toast.success(
      <div className="flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
        <span className="font-semibold text-primary">{t("gloabl.addedToCart")}</span>
      </div>,
      {
        className: "!bg-card !border-2 !border-primary/50 !rounded-xl !shadow-xl !backdrop-blur-sm",
        bodyClassName: "!text-primary !font-semibold",
        progressClassName: "!bg-primary",
        hideProgressBar: false,
        onClick: () => {
          navigate(`/branches/${params.branch_id}/cart`);
        },
      }
    );
    if (params.branch_id) {
      CalcQuantity(Number(params.branch_id));
    }
  };

  // حساب المجموع الكلي: (سعر المنتج + أسعار الخيارات) × الكمية
  const totalPrice = React.useMemo(() => {
    const basePrice = product.price_offer || product.price;
    let optionsPrice = 0;
    
    if (optionsSelected && optionsSelected.length > 0) {
      optionsSelected.forEach((opt: any) => {
        if (opt.subOption && opt.subOption.price) {
          optionsPrice += opt.subOption.price;
        }
      });
    }
    
    return (basePrice + optionsPrice) * quantity;
  }, [product.price, product.price_offer, optionsSelected, quantity]);

  return (
    <>
      <Visibility visible={!itemLoading}>
        <div className="min-h-screen flex flex-col bg-background relative">
          {/* Red Gradient Background - Covers entire page including header */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/90 via-primary/50 to-primary/25 pointer-events-none z-0"></div>

          {/* Header Section - Transparent with gradient */}
          <div className="sticky top-0 z-50 text-onPrimary  backdrop-blur-sm">
            <div className="flex items-center justify-between px-4 py-4">
              {/* Back Button */}
              <button
                onClick={handleBack}
                className="p-2 rounded-lg hover:bg-onPrimary/10 transition-all duration-300 active:scale-95 text-onPrimary"
              >
                {lang === "ar" ? (
                  <FaAngleRight className="w-5 h-5 md:w-6 md:h-6" />
                ) : (
                  <FaAngleLeft className="w-5 h-5 md:w-6 md:h-6" />
                )}
              </button>

              {/* Spacer for symmetry */}
              <div className="w-9 md:w-10" />
            </div>
          </div>

          {/* Product Section */}
          <div className="relative z-10 pb-6 overflow-hidden">

            <div className="px-4 pt-6 pb-4">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8">
                {/* Product Image - Left Side with Tags */}
                <div className="flex-shrink-0 w-full md:w-auto relative">
                  <div className="relative">
                    <img
                      className="w-56 h-56 md:w-64 md:h-64 rounded-full object-cover shadow-lg border-3 border-onPrimary/30"
                      src={`${IMG_URl}${product.image}`}
                      alt={product[name as keyof ProductModel] as string}
                    />
                    {/* Tags Overlay - Top Right */}
                  </div>
                </div>

                {/* Product Info - Right Side */}
                <div className="flex-1 space-y-3 md:space-y-4">
                  {/* Product Name - Same Line */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      <div className="absolute -top-2 -right-2 md:-top-3 md:-right-3 flex flex-col gap-1.5 z-10">
                        <Visibility visible={!!product.new}>
                          <span className="text-[10px] md:text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-md font-semibold shadow-sm border border-amber-200">
                            {t("gloabl.new")}
                          </span>
                        </Visibility>
                        <Visibility visible={!!product.popular}>
                          <span className="text-[10px] md:text-xs px-2 py-1 bg-onPrimary text-primary rounded-md font-semibold shadow-sm">
                            {t("gloabl.common")}
                          </span>
                        </Visibility>
                      </div>
                      {product?.[name as keyof ProductModel]?.toString()}
                    </h2>
                    {product.description_ar && (
                      <p className="text-lg md:text-xl font-semibold text-foreground">
                        {product[description as keyof ProductModel]?.toString()}
                      </p>
                    )}
                  </div>

                  {/* Price Section */}
                  <div className="space-y-2">
                    <p className="text-sm md:text-base text-foreground/70">
                      {t("gloabl.totalPrice") || "السعر"}
                    </p>
                    <div className="flex items-center gap-2">
                      <p
                        className={`text-2xl md:text-3xl font-bold flex items-center gap-1 text-foreground ${product.price_offer > 0
                          ? "line-through decoration-primary decoration-2 text-foreground/50"
                          : ""
                          }`}
                      >
                        <UAEDirhamIcon className="w-5 h-5 md:w-6 md:h-6" />
                        {product.price}
                      </p>
                      {product.price_offer > 0 && (
                        <p className="text-2xl md:text-3xl font-bold flex items-center gap-1 text-primary">
                          <UAEDirhamIcon className="w-5 h-5 md:w-6 md:h-6" />
                          {product.price_offer}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Quantity Selector */}

                  <div className="flex items-center gap-3 bg-onPrimary/10 rounded-xl p-2 w-fit border-2 border-onPrimary/30">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="w-10 h-10 rounded-lg bg-onPrimary/20 hover:bg-onPrimary/30 text-onPrimary font-bold text-lg flex items-center justify-center transition-colors"
                    >
                      -
                    </button>
                    <span className="w-12 text-center font-bold text-lg bg-onPrimary text-primary rounded-lg py-2">
                      {String(quantity).padStart(2, "0")}
                    </span>
                    <button
                      onClick={() => setQuantity((q) => q + 1)}
                      className="w-10 h-10 rounded-lg bg-onPrimary/20 hover:bg-onPrimary/30 text-onPrimary font-bold text-lg flex items-center justify-center transition-colors"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Options Section */}
          <Visibility visible={product.options && product.options.length > 0}>
            <div className="bg-background px-4 py-4 border-t border-border/40">
              <div className="max-w-4xl mx-auto">
                <OptionsProduct
                  options={product.options}
                  name={name}
                  price={product.price_offer || product.price}
                  setOptionsSelected={setOptionsSelected}
                  optionsSelected={optionsSelected}
                />
              </div>
            </div>
          </Visibility>



          {/* Add to Cart Button - Fixed at Bottom */}
          <Visibility visible={activeOrder === 1}>
            <div className="sticky bottom-0 z-50 bg-background border-t border-border/50 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] p-4">
              <button
                onClick={handleAddToCart}
                className="w-full bg-background border-2 border-primary rounded-full py-4 px-6 flex items-center justify-center gap-3 hover:bg-primary/5 transition-all duration-300 group"
              >
                <ShoppingCart className="w-5 h-5 text-primary group-hover:scale-110 transition-transform" />
                <span className="text-primary font-bold text-base md:text-lg flex items-center gap-1">
                  {t("gloabl.AddToCart")}
                  <span className="flex items-center gap-1">
                    (<UAEDirhamIcon className="w-4 h-4" />
                    {totalPrice.toFixed(1)})
                  </span>
                </span>
              </button>
            </div>
          </Visibility>
        </div>
      </Visibility>

      <Visibility visible={itemLoading}>
        <RenderProduct />
      </Visibility>
    </>
  );
}

export default OldProductScreen;

const RenderProduct = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header Skeleton */}
      <div className="bg-background border-b sticky top-0 z-50">
        <div className="flex items-center justify-between px-4 py-4">
          <Skeleton className="w-9 h-9 rounded-lg" />
          <Skeleton className="w-32 h-6" />
          <Skeleton className="w-9 h-9 rounded-lg" />
        </div>
      </div>

      {/* Product Section Skeleton */}
      <div className="relative bg-background pb-8">
        <div className="relative z-10 px-4 pt-6 pb-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-8">
            <Skeleton className="w-48 h-48 md:w-64 md:h-64 rounded-full" />
            <div className="flex-1 space-y-4 w-full">
              <Skeleton className="w-48 h-8" />
              <Skeleton className="w-32 h-6" />
              <Skeleton className="w-40 h-10" />
              <Skeleton className="w-32 h-12 rounded-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Description Skeleton */}
      <div className="bg-background px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-3">
          <Skeleton className="w-full h-4" />
          <Skeleton className="w-full h-4" />
          <Skeleton className="w-3/4 h-4" />
        </div>
      </div>

      {/* Button Skeleton */}
      <div className="sticky bottom-0 z-50 bg-background border-t p-4">
        <Skeleton className="w-full h-14 rounded-full" />
      </div>
    </div>
  );
};
